import React, { useEffect, useMemo, useState } from 'react';
import { sendContactEmail } from '../api/contact_api';
import { getContactFormSubmitError } from '../utils/errorHandler';

export default function HeaderReportErrorPopup({ isOpen, onClose }) {
  const [formValues, setFormValues] = useState({
    fullName: '',
    emailAddress: '',
    accountName: '',
    contentLink: '',
    explanation: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  const setFieldValue = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFieldValue(field, value);
  };

  const validateForm = () => {
    const validations = [
      { valid: !!formValues.fullName.trim(), message: 'Full name is required.' },
      { valid: !!formValues.emailAddress.trim(), message: 'Email is required.' },
      { valid: emailRegex.test(formValues.emailAddress.trim()), message: 'Please enter a valid email address.' },
      { valid: !!formValues.accountName.trim(), message: 'Account name is required.' },
      { valid: !!formValues.contentLink.trim(), message: 'Link to content is required.' },
      { valid: !!formValues.explanation.trim(), message: 'Explanation is required.' },
    ];

    const firstInvalid = validations.find((item) => !item.valid);
    if (firstInvalid) {
      setPopupMessage({ title: 'Please check the form', text: firstInvalid.message });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSending(true);
      setPopupMessage(null);
      await sendContactEmail({
        fullName: formValues.fullName.trim(),
        emailAddress: formValues.emailAddress.trim(),
        accountName: formValues.accountName.trim(),
        contentLink: formValues.contentLink.trim(),
        explanation: formValues.explanation.trim(),
      });
      setFormValues({
        fullName: '',
        emailAddress: '',
        accountName: '',
        contentLink: '',
        explanation: '',
      });
      setPopupMessage({
        title: 'Message sent.',
        text: 'Thanks, we received your report and will review it shortly.',
      });
    } catch (error) {
      setPopupMessage(getContactFormSubmitError(error));
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="header-report-popup-overlay" onClick={onClose}>
      <div className="header-report-popup" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="header-report-popup-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {popupMessage ? (
          <div className="header-report-success-popup" role="status" aria-live="polite">
            <button
              type="button"
              className="header-report-success-popup-close"
              onClick={() => setPopupMessage(null)}
              aria-label="Dismiss message"
            >
              ×
            </button>
            <strong>{popupMessage.title}</strong>
            <span>{popupMessage.text}</span>
          </div>
        ) : null}

        <div className="header-report-popup-content">
          <h3>Report an Error</h3>
          <p>Found a mistake? Let us know so we can do our best to fix it!</p>

          <div className="header-report-form">
            <section className="header-report-section" aria-labelledby="header-report-personal-info-title">
              <h4 id="header-report-personal-info-title">Personal Information</h4>
              <div className="header-report-inline-fields">
                <div className="header-report-field-group">
                  <label htmlFor="report-full-name">Full name</label>
                  <input
                    id="report-full-name"
                    type="text"
                    placeholder="e.g. John Rozenberg"
                    value={formValues.fullName}
                    onChange={handleFieldChange('fullName')}
                  />
                </div>

                <div className="header-report-field-group">
                  <label htmlFor="report-email">Email</label>
                  <input
                    id="report-email"
                    type="email"
                    placeholder="e.g. user@mail.com"
                    value={formValues.emailAddress}
                    onChange={handleFieldChange('emailAddress')}
                  />
                </div>
              </div>
            </section>

            <section className="header-report-section" aria-labelledby="header-report-details-title">
              <h4 id="header-report-details-title">Report Information</h4>

              <div className="header-report-inline-fields">
                <div className="header-report-field-group">
                  <label htmlFor="report-account-name">Account name</label>
                  <input
                    id="report-account-name"
                    type="text"
                    placeholder="e.g. @user2058"
                    value={formValues.accountName}
                    onChange={handleFieldChange('accountName')}
                  />
                </div>

                <div className="header-report-field-group">
                  <label htmlFor="report-content-link">Link to content</label>
                  <input
                    id="report-content-link"
                    type="text"
                    placeholder="https://"
                    value={formValues.contentLink}
                    onChange={handleFieldChange('contentLink')}
                  />
                </div>
              </div>

              <div className="header-report-field-group">
                <label htmlFor="report-explanation">Explanation</label>
                <textarea
                  id="report-explanation"
                  rows={4}
                  value={formValues.explanation}
                  onChange={handleFieldChange('explanation')}
                  placeholder="Write here..."
                />
                <span className="header-report-explanation-hint">
                  Please explain why the specific content featured is not accurate
                </span>
              </div>
            </section>
          </div>

          <button type="button" className="header-report-send-btn" onClick={handleSubmit} disabled={isSending}>
            {isSending ? (
              <>
                <span className="spinner-small database-alert-send-spinner" aria-hidden="true" />
                Sending...
              </>
            ) : 'Submit'}
          </button>
        </div>

        <img
          className="header-report-popup-illustration"
          src="/images/report-an-error-illustartion.png"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
